<template>
  <b-tabs>
    <b-tab title="Design">
      <DesignEditor
          v-if="loaded"
          :entity-id="designId"
          :mode="mode"
          :disable-close="disableClose"
          :inspiration-form="form"
          :inspiration-form-config="formConfig"
          @cancel="cancel"
          @saved-tab="savedTabState"
          :form-type="DESIGN_FORM_TYPE"
          @images-list-changed="updateImagesList"
          ref="formDesignEditor"/>
    </b-tab>
    <b-tab title="Marketing Description" :disabled="tabDisabled">
      <form-edit-activity :form-type="INSPIRATION_FORM_TYPE"
                          ref="formMarketingDescriptionEditor"
                          :mode="mode"
                          :entity-id="entityId"
                          @form-created="formCreated"
                          :context="context"
                          :disable-close="disableClose"
                          :preset-fields="presetFields"
                          @saved="save"
      >
      </form-edit-activity>
    </b-tab>
    <b-tab title="Wishlist" :disabled="mode === 'NEW'">
      <form-edit-activity :form-type="WISHLIST_FORM_TYPE"
                          ref="formWishlistEditor"
                          v-if="wishlistId"
                          :mode="mode"
                          :entity-id="wishlistId"
                          @form-created="wishlistFormCreated"
                          :context="context"
                          :disable-close="disableClose"
      >
      </form-edit-activity>
    </b-tab>
  </b-tabs>
</template>

<script>
import Activity from "@/activity/Activity";
import Rpc from "@/Rpc";
import FormsListActivity from "@/activity/templates/layouts/forms/FormsListActivity";
import FormsLoader from "@/components/forms/FormsLoader";
import FormEditActivity from "@/activity/templates/layouts/forms/FormEditActivity";
import FormMixin from "@/components/forms/customizations/mixins/FormMixin";
import ReachInput from "@/components/inputs/ReachInput";
import Inspiration from "@/server/com/silkplm/model/plm/Inspiration";
import Design from "@/server/com/silkplm/model/plm/Design";
import DesignEditor from "@/components/forms/customizations/plm/DesignEditor.vue";
import ActivityManager from "@/activity/ActivityManager";
import DialogManager from "@/utils/DialogManager";
import RpcInputValidationException from "@/server/com/silkplm/engine/rpc/exceptions/RpcInputValidationException";
import RoutingKeys from "@/server/RoutingKeys";
import Wishlist from "@/server/com/silkplm/model/plm/Wishlist";

const INSPIRATION_FORM_TYPE = Inspiration;
const WISHLIST_FORM_TYPE = Wishlist;
const DESIGN_FORM_TYPE = Design;

export default {
  name: "InspirationEditor",
  components: {DesignEditor, ReachInput, FormMixin, FormEditActivity, FormsListActivity},
  permission: () => FormsLoader.formPermission(INSPIRATION_FORM_TYPE),
  design_permission: () => FormsLoader.formPermission(DESIGN_FORM_TYPE),
  wishlist_permission: () => FormsLoader.formPermission(WISHLIST_FORM_TYPE),

  props: {
    entityId: {default: null},
    mode: {type: String, default: "NEW"},
    context: {default: null},
    presetFields: {default: null},
  },

  created() {
  },

  async mounted() {
    if (this.mode !== "NEW" && this.entityId) {
      this.designId = await Rpc.plm.services.MarketingService.findDesignId(this.entityId);
      this.wishlistId = await Rpc.plm.services.MarketingService.findWishlistId(this.entityId);
    }
    this.loaded = true;

    this.$subscribe(RoutingKeys.INSPIRATION_GENERATING_STATE + "." + this.entityId, async message => {
      this.disableClose = message.generatingImage;
    });
  },

  watch: {
    entityId: async function (val) {
      if (val !== null) {
        this.designId = await Rpc.plm.services.MarketingService.findDesignId(this.entityId);
        this.wishlistId = await Rpc.plm.services.MarketingService.findWishlistId(this.entityId);
      }
    },
  },

  methods: {
    updateImagesList(newList) {
      this.imagesList = newList;
      this.tabDisabled = !(this.imagesList && this.imagesList.length > 0);
    },

    async save(data) {
      try {
        this.savedTab = true;
        this.designForm = this.$refs.formDesignEditor._data.form;
        this.designForm.fields.inspirationId._value = data.id;
        this.designEntityInstance = await this.designForm.saveEntity(false, this.regionCode);
        if (!this.designForm.isValid || this.designForm.temporaryListsErrors) {
          setTimeout(() => {
            this.$emit("unsaved-changes");
          });
          DialogManager.infoHtml($tr('saveFailed', 'Save failed'),
              $tr('entityEditForm.records.arent.save', 'Some records aren\'t save successfully, fix or delete broken records')).then();
          return false;
        }
        this.designForm.setFieldsReadOnly(true, this.mode, this.fieldsManagedByParent);
        this.$emit('saved', this.designEntityInstance);

      } catch (e) {
        if (e instanceof RpcInputValidationException) {
          let errorSummary = "";
          for (let fieldName in e.fields) {
            let field = e.fields[fieldName];
            if (field['errorMessage']) {
              errorSummary += field['errorMessage'] + "\n";
            }
          }
          if (e.errorMessage) {
            errorSummary += e.errorMessage;
          }

          await DialogManager.infoHtml($tr("entityEditForm.record.cant.save", "Record can't be saved"), errorSummary);
        } else {
          console.log(e);
        }
        return false;
      }
    },

    savedTabState(val){
      this.savedTab = val;
    },

    ...Activity.methods,
    cancel() {
      this.close();
    },
    async canClose() {
      this.designGallery = this.$refs.formDesignEditor._data.validatedField._value;
      let inspirationIsValid = this.$refs.formMarketingDescriptionEditor.$refs.list.isValid();

      if (this.mode === "NEW" && this.designGallery && this.designGallery.imagesList.length > 0 && !this.savedTab && inspirationIsValid) {
        let action = await DialogManager.closeUnsaved(true);
        if (action === 'close' || action === 'save') {
          await this.$refs.formDesignEditor.removeGeneratingTagImages()
          return true;
        }
      } else {
        await this.$refs.formDesignEditor.removeGeneratingTagImages()
        return this.$refs.formMarketingDescriptionEditor.canClose();
      }
    },

    /** @type {FormModel} form */
    async formCreated(form) {
      this.form = form;
    },

    async wishlistFormCreated(form) {
      this.wishlistForm = form;
    },
  }
  ,

  data: function () {
    return {
      INSPIRATION_FORM_TYPE,
      DESIGN_FORM_TYPE,
      WISHLIST_FORM_TYPE,
      /** @type {FormModel} */
      form: null,
      designForm: null,
      wishlistForm: null,
      savedTab: false,
      designEntityInstance: null,
      validatedField: null,
      localImagesList: null,
      readOnly: true,
      imagesList: [],
      designGallery: null,
      designId: null,
      wishlistId: null,
      loaded: false,
      disableClose: false,
      tabDisabled: false,
      formConfig: FormsLoader.detectFormConfig(INSPIRATION_FORM_TYPE),
      formConfigForDesign: FormsLoader.detectFormConfig(DESIGN_FORM_TYPE),
      formConfigForWishlist: FormsLoader.detectFormConfig(WISHLIST_FORM_TYPE),
      formId: FormsLoader.detectFormConfig(INSPIRATION_FORM_TYPE).formId,
      formIdForDesign: FormsLoader.detectFormConfig(DESIGN_FORM_TYPE).formId,
      formIdForWishlist: FormsLoader.detectFormConfig(WISHLIST_FORM_TYPE).formId,
    }
  }
}
</script>
